import React, { useEffect, useRef, useState } from 'react';
import './registry.scss';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import fileDownload from 'js-file-download';
import { isMobile } from 'react-device-detect';
import * as ReactDOM from 'react-dom';
import {
  getJsonFromStorage,
  getPaginationOpts,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import { loadRegistryExportList } from '../actions';
import { getSystemData } from '../../System/reducer';
import { getRegisterData } from '../reducer';
import Pagination from '../../../components/base/Pagination/Pagination';
import { Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import TextInput from '../../../components/fields/TextInput/TextInput';
import { dateTimeFormatter, statusFormatter, stringFormatter } from '../../../utils/formatters';
import Grid from '../../../components/base/Grid/Grid';
import { FGIS_TAXI_TYPE_NAMES, MAIS_EGU_LOG_STATUS_NAMES } from '../../../configs';
import RangeDatePicker from '../../../components/fields/RangeDatePicker/RangeDatePicker';
import getDefaultDataRange from '../../../utils/getDefaultDataRange';
import { REGION_REGISTRY_MODULE } from '../../../configs/moduleNames';
import getDefaultSort from '../../../utils/getDefaultSort';
import TimerLoader from '../../../components/base/Loader/TimerLoader';
import MobileRegistryExportFooter from './MobileRegistryExportFooter';
import PropertyGrid from '../../../components/base/Grid/PropertyGrid';
import TextSearchInput from '../../../components/fields/TextInput/TextSearchInput';

export default function RegistryExportPage() {
  const blockRef = useRef(null);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const store = useStore();
  const { updatePeriod } = useSelector(getSystemData);
  const {
    loadingRegistryExportList,
    registryExportList = [],
    registryExportListMeta = {},
    loadingRegistryHistory,
  } = useSelector(getRegisterData);
  const defaultDate = getDefaultDataRange();
  const defaultFilterValues = {
    created_at_from: defaultDate[0], created_at_to: defaultDate[1], search: '', created_at: '',
  };
  const {
    reset, getValues, control, setValue, watch,
  } = useForm({ defaultValues: getJsonFromStorage('registry-export-filter', defaultFilterValues) });

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[REGION_REGISTRY_MODULE].registryExportListMeta, opts); // костыль внедрен из за потери контекста в updatePeriod. Он сохранял первую функцию
    const requestData = {
      ...paginationOpts,
      filter: getValues(),
      sort,
    };
    const p1 = dispatch(loadRegistryExportList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const columns = [
    {
      dataField: 'created_at',
      text: 'Дата и время',
      headerClassName: 'date-with-time',
      formatter: dateTimeFormatter,
    },
    {
      dataField: 'extra',
      text: 'Тип запроса',
      formatter: a => stringFormatter(a?.type, 'fgis.taxi.log.type', FGIS_TAXI_TYPE_NAMES),
    },
    {
      dataField: 'message_id',
      text: 'Message ID',
    },
    {
      dataField: 'status',
      text: 'Статус',
      formatter: a => statusFormatter(a, 'mais.egu.log.status', MAIS_EGU_LOG_STATUS_NAMES),
    },
    {
      dataField: 'requested_at',
      text: 'Отправлено',
      headerClassName: 'date-with-time',
      formatter: dateTimeFormatter,
    },
    {
      dataField: 'xml_request',
      text: 'Запрос',
      headerClassName: 'report-column',
      dataClassName: 'reload-column-data',
      formatter: a => <Icons className={classNames('xml-loader', { disable: !a })} onClick={() => fileDownload(a, 'request.xml')} icon="xml_report" customIcon />,
    },
    {
      dataField: 'responded_at',
      text: 'Получено',
      headerClassName: 'date-with-time',
      formatter: dateTimeFormatter,
    },
    {
      dataField: 'xml_response',
      text: 'Ответ',
      headerClassName: 'report-column',
      dataClassName: 'reload-column-data',
      formatter: a => <Icons className={classNames('xml-loader', { disable: !a })} onClick={() => fileDownload(a, 'response.xml')} icon="xml_report" customIcon />,
    },
    {
      dataField: 'extra',
      text: 'Описание ошибки',
      formatter: a => a?.error_mgs,
    },
  ];

  const [sort, setSort] = useState(getDefaultSort(columns));

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  useEffect(() => { loadData(); }, [sort]);

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  useEffect(() => {
    const subscription = watch(values => loadData({ page: 1 }));
    return () => subscription.unsubscribe();
  }, [watch]);

  const renderFooter = () => {
    const element = document.getElementById('app-content-footer');
    if (!element) {
      return null;
    }
    return ReactDOM.createPortal(<MobileRegistryExportFooter
      control={control}
      defaultFilterValues={defaultFilterValues}
      getValues={getValues}
      reset={reset}
    />, element);
  };

  return (
    <div className="page application-page">
      <TimerLoader global loading={loadingRegistryExportList || loadingRegistryHistory} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.region.registry.export' })}</div>
        {!isMobile ? (
          <FilterRow>
            <FieldDTO
              control={control}
              component={TextSearchInput}
              dtoObject={{
                name: 'search',
                type: String,
                labelKey: 'search',
              }}
              className="no-border"
            />
            <FieldDTO
              control={control}
              component={RangeDatePicker}
              dtoObject={{
                name: 'created_at',
                type: String,
                labelKey: 'created_at',
              }}
              usePortal
              placeholder="Дата"
              changeFormat="YYYY-MM-DD"
              inputProps={{
                value: [getValues('created_at_from'), getValues('created_at_to')],
                onChange: e => {
                  const v = e.target.value;
                  setValue('created_at_from', v[0]);
                  setValue('created_at_to', v[1]);
                },
              }}
              className="no-border"
            />
          </FilterRow>
        ) : null}
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          {!isMobile ? (
            <div className="grid-wrapper frame">
              <div className="custom-scroll-theme sticky-table-wrapper">
                <Grid
                  data={registryExportList}
                  columns={columns}
                  name="application-list"
                  changeSort={changeSort}
                  sort={sort}
                />
              </div>
            </div>
          ) : (
            <PropertyGrid
              data={registryExportList}
              columns={columns}
              name="application-list"
              changeSort={changeSort}
              sort={sort}
            />
          )}
          <Pagination
            meta={registryExportListMeta}
            list={registryExportList}
            handleChangeMeta={loadData}
          />
        </div>
      </div>
      {isMobile ? renderFooter() : null}
    </div>
  );
}
